import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type { InstructorWithProfile, SpecialtyType, ProfileRow } from '@/types/database'

// Shape composto do select com joins opcionais que esse hook usa. Centraliza
// os campos extra e elimina `as any` no consumo.
interface InstructorWithExtras extends InstructorWithProfile {
  specialties?: { specialty: SpecialtyType }[] | null
  availability?: { day_of_week: number; start_time: string; end_time: string }[] | null
  reviews?: Array<{
    id: string
    rating: number
    comment: string | null
    instructor_reply: string | null
    instructor_reply_at: string | null
    created_at: string
    student: Pick<ProfileRow, 'full_name' | 'avatar_url'> | null
  }> | null
}

// ─── Filter shape ─────────────────────────────────────────────────────────────

type SortOption = 'rating_desc' | 'price_asc' | 'price_desc' | 'experience_desc'

interface InstructorFilters {
  city?: string
  specialties?: string[]
  minRating?: number
  minPrice?: number
  maxPrice?: number
  search?: string
  sort?: SortOption
  availableToday?: boolean
  availableThisWeek?: boolean
  availableDate?: string // "YYYY-MM-DD"
  womenOnly?: boolean
  // I1 — filtros avançados
  query?: string          // alias para search (busca por nome)
  onlyFemale?: boolean    // alias para womenOnly
  onlyPro?: boolean       // is_pro_instructor
  availability?: {        // dia_da_semana + período
    dayOfWeek: number
    period: 'morning' | 'afternoon' | 'evening'
  }
  vehicle?: string        // tipo de veículo (ex: 'manual', 'automatico')
  // P1 — filtros avançados
  minExperienceYears?: number
}

// ─── Query key factory ────────────────────────────────────────────────────────

const instructorKeys = {
  all: ['instructors'] as const,
  list: (filters?: InstructorFilters) =>
    [...instructorKeys.all, 'list', filters ?? {}] as const,
  detail: (slug: string) => [...instructorKeys.all, 'detail', slug] as const,
}

// ─── Fetchers ─────────────────────────────────────────────────────────────────

// Mapa de período para intervalos de horas
const PERIOD_HOURS: Record<'morning' | 'afternoon' | 'evening', [number, number]> = {
  morning:   [6,  12],
  afternoon: [12, 18],
  evening:   [18, 23],
}

async function fetchInstructors(
  filters?: InstructorFilters,
): Promise<InstructorWithExtras[]> {
  // Normaliza aliases
  const normalizedFilters: InstructorFilters | undefined = filters
    ? {
        ...filters,
        search:    filters.query ?? filters.search,
        womenOnly: filters.onlyFemale ?? filters.womenOnly,
      }
    : undefined

  const f = normalizedFilters

  const needsAvailability = f?.availableToday || f?.availableThisWeek || f?.availableDate || f?.availability

  // Campos públicos do perfil — exclui phone para não vazar PII na listagem
  const publicProfileFields = 'id, full_name, avatar_url, role, city, state, is_pro_instructor, created_at'

  let query = supabase
    .from('instructors')
    .select(
      needsAvailability
        ? `*, profile:profiles(${publicProfileFields}), specialties:instructor_specialties(specialty), availability:availability(day_of_week, start_time, end_time)`
        : `*, profile:profiles(${publicProfileFields}), specialties:instructor_specialties(specialty)`,
    )
    .eq('status', 'approved')

  if (f?.minRating !== undefined) {
    query = query.gte('rating_avg', f.minRating)
  }
  if (f?.minPrice !== undefined) {
    query = query.gte('hourly_rate', f.minPrice)
  }
  if (f?.maxPrice !== undefined) {
    query = query.lte('hourly_rate', f.maxPrice)
  }
  if (f?.womenOnly) {
    query = query.eq('is_woman_instructor', true)
  }

  const sort = f?.sort ?? 'rating_desc'
  if (sort === 'price_asc') {
    query = query.order('hourly_rate', { ascending: true })
  } else if (sort === 'price_desc') {
    query = query.order('hourly_rate', { ascending: false })
  } else if (sort === 'experience_desc') {
    query = query.order('total_lessons', { ascending: false })
  } else {
    query = query.order('rating_avg', { ascending: false })
  }

  const { data, error } = await query
  if (error) throw new Error(error.message)

  let results = (data ?? []) as unknown as InstructorWithExtras[]

  // Instrutores Pro sempre aparecem primeiro (ordering client-side — garantido
  // independente do suporte do PostgREST a ordering em tabelas embarcadas).
  results = results.sort((a, b) => {
    const aPro = a.profile?.is_pro_instructor ? 1 : 0
    const bPro = b.profile?.is_pro_instructor ? 1 : 0
    // I1: onlyPro filtra apenas pro
    return bPro - aPro
  })

  // I1: filtro onlyPro
  if (f?.onlyPro) {
    results = results.filter((inst) => inst.profile?.is_pro_instructor === true)
  }

  // Client-side filters
  if (f?.search) {
    const term = f.search.toLowerCase()
    results = results.filter((inst) => {
      const name = inst.profile?.full_name ?? ''
      const bio  = inst.bio ?? ''
      return name.toLowerCase().includes(term) || bio.toLowerCase().includes(term)
    })
  }

  if (f?.city) {
    const term = f.city.toLowerCase()
    results = results.filter((inst) =>
      (inst.profile?.city ?? '').toLowerCase().includes(term),
    )
  }

  if (f?.specialties && f.specialties.length > 0) {
    results = results.filter((inst) => {
      const raw = inst.specialties ?? []
      const instSpecialties = raw.map((s) => s.specialty)
      return f.specialties!.some((sp) => instSpecialties.includes(sp as SpecialtyType))
    })
  }

  // I1: filtro de veículo (client-side via vehicle_model)
  if (f?.vehicle) {
    const vehicleTerm = f.vehicle.toLowerCase()
    results = results.filter((inst) =>
      (inst.vehicle_model ?? '').toLowerCase().includes(vehicleTerm),
    )
  }

  // P1: filtro de anos de experiência mínimos
  if (f?.minExperienceYears !== undefined && f.minExperienceYears > 0) {
    results = results.filter((inst) =>
      (inst.years_experience ?? 0) >= f.minExperienceYears!,
    )
  }

  // ── Availability filtering with exception awareness ──────────────────────────

  async function applyAvailabilityFilter(
    list: InstructorWithExtras[],
    targetDow: number,
    targetDate: string | null,
  ): Promise<InstructorWithExtras[]> {
    const allIds = list.map((i) => i.id)
    const exceptionMap: Record<string, boolean> = {}

    if (targetDate && allIds.length > 0) {
      const { data: exceptions } = await supabase
        .from('availability_exceptions')
        .select('instructor_id, is_available')
        .eq('date', targetDate)
        .in('instructor_id', allIds)

      for (const ex of exceptions ?? []) {
        exceptionMap[ex.instructor_id] = ex.is_available
      }
    }

    return list.filter((inst) => {
      if (exceptionMap[inst.id] !== undefined) return exceptionMap[inst.id]
      const av = inst.availability ?? []
      return av.some((a) => a.day_of_week === targetDow)
    })
  }

  if (f?.availableToday) {
    const today = new Date()
    results = await applyAvailabilityFilter(results, today.getDay(), today.toISOString().slice(0, 10))
  }

  if (f?.availableThisWeek && !f?.availableToday) {
    const todayDow = new Date().getDay()
    const remainingDays = Array.from({ length: 7 }, (_, i) => (todayDow + i) % 7)
    results = results.filter((inst) => {
      const av = inst.availability ?? []
      return av.some((a) => remainingDays.includes(a.day_of_week))
    })
  }

  if (f?.availableDate && !f?.availableToday && !f?.availableThisWeek) {
    const dow = new Date(f.availableDate + 'T12:00:00').getDay()
    results = await applyAvailabilityFilter(results, dow, f.availableDate)
  }

  // I1: filtro de disponibilidade por dia + período
  if (f?.availability) {
    const { dayOfWeek, period } = f.availability
    const [startH, endH] = PERIOD_HOURS[period]
    results = results.filter((inst) => {
      const av = inst.availability ?? []
      return av.some((a) => {
        if (a.day_of_week !== dayOfWeek) return false
        const slotStartH = Number(a.start_time.split(':')[0])
        const slotEndH   = Number(a.end_time.split(':')[0])
        // Verifica overlap com o período solicitado
        return slotStartH < endH && slotEndH > startH
      })
    })
  }

  return results
}

async function fetchInstructor(slug: string): Promise<InstructorWithExtras> {
  const { data, error } = await supabase
    .from('instructors')
    .select(
      `
      *,
      profile:profiles(id, full_name, avatar_url, role, city, state, is_pro_instructor, created_at),
      specialties:instructor_specialties(specialty),
      availability:availability(day_of_week, start_time, end_time),
      reviews:reviews(
        id,
        rating,
        comment,
        instructor_reply,
        instructor_reply_at,
        created_at,
        student:profiles!reviews_student_id_fkey(
          full_name,
          avatar_url
        )
      )
      `,
    )
    .eq('slug', slug)
    .eq('status', 'approved')
    .order('created_at', { referencedTable: 'reviews', ascending: false })
    .limit(10, { referencedTable: 'reviews' })
    .single()

  if (error) throw new Error(error.message)
  return data as unknown as InstructorWithExtras
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useInstructors(filters?: InstructorFilters) {
  const query = useQuery<InstructorWithExtras[], Error>({
    queryKey: instructorKeys.list(filters),
    queryFn: () => fetchInstructors(filters),
    staleTime: 1000 * 60 * 3,
    retry: 0,
  })

  return {
    instructors: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}

function useInstructor(slug: string) {
  return useQuery<InstructorWithExtras, Error>({
    queryKey: instructorKeys.detail(slug),
    queryFn: () => fetchInstructor(slug),
    enabled: Boolean(slug),
    staleTime: 1000 * 60 * 5,
  })
}

export { useInstructors, useInstructor, instructorKeys }
export type { InstructorFilters, SortOption, InstructorWithExtras }
